import classNames from 'classnames';

import React, {
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  ReactElement,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import styles from './scalable-collection.scss';

const COMPONENT_ID = 'modulor-scalable-collection';

const GRID_GAP = 8;
const GRID_MARGIN = 8;
const GRID_SIZES = {
  9: 'large',
  6: 'medium',
  5: 'small',
  3: 'tiny',
};

type Props = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    children: ReadonlyArray<ReactElement>;
    dataTestId?: string;
    fallback?: ReactElement;
  };

export const ScalableCollection: ForwardRefExoticComponent<Props> = forwardRef<
  HTMLDivElement,
  Props
>(({ children, className, fallback, dataTestId = COMPONENT_ID, ...rest }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [itemHeight, setItemHeight] = useState<number | string | null>(null);
  const [gridSize, setGridSize] = useState<string>('large');
  const classes = classNames(className, styles.container, styles[gridSize]);

  const getCellNumber = (width: number) => {
    if (width >= 1120) return 9;
    if (width >= 720) return 6;
    if (width >= 600) return 5;
    return 3;
  };

  const defineCellHeight = useCallback(() => {
    const width = containerRef.current.offsetWidth;
    const cellNumber = getCellNumber(width);
    const gapNumber = cellNumber - 1;

    setItemHeight((width - 2 * GRID_MARGIN - gapNumber * GRID_GAP) / cellNumber);
    setGridSize(GRID_SIZES[cellNumber]);
  }, []);

  useImperativeHandle(ref, () => containerRef.current);

  useEffect(() => {
    if (containerRef?.current?.offsetWidth) {
      defineCellHeight();
    }
  }, [defineCellHeight]);

  useEffect(() => {
    window.addEventListener('resize', defineCellHeight);
    return () => window.removeEventListener('resize', defineCellHeight);
  }, [defineCellHeight]);

  if (fallback && !itemHeight) {
    return (
      <div
        ref={containerRef}
        data-id={dataTestId}
        data-testid={dataTestId}
        data-component-name={COMPONENT_ID}>
        {fallback}
      </div>
    );
  }

  return (
    <div
      className={classes}
      ref={containerRef}
      data-id={dataTestId}
      data-testid={dataTestId}
      data-component-name={COMPONENT_ID}
      {...rest}>
      {itemHeight &&
        React.Children.map(children, (child, index) =>
          React.cloneElement(child, {
            style: { height: itemHeight },
            height: itemHeight,
            dataTestId: `${dataTestId}-item-${index}`,
            className: classNames(styles.item, {
              [styles.single]: index % 2 === 0 && index === React.Children.count(children) - 1,
            }),
          }),
        )}
    </div>
  );
});

ScalableCollection.displayName = 'ScalableCollection';
